import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import {
  ConversationFilterEnum,
  ConversationPriorityEnum,
  ConversationTypeEnum,
  DateRangeEnum,
} from 'common/enums';
import { Conversation } from 'common/types';
import { alyviaCoreServiceApi } from 'utils/constants';
import { useChatStore } from "store/useChatStore";


interface FetchConversationsParams {
  searchTerm?: string;
  priority?: ConversationPriorityEnum;
  type?: ConversationTypeEnum;
  limit?: number;
  cursor?: string;
}

type FetchConversationsResponse = {
  data: Conversation[];
  meta: {
    hasMore: boolean;
    nextCursor: string;
    stats: Record<
      Exclude<
        ConversationFilterEnum,
        ConversationFilterEnum.ALL | ConversationFilterEnum.NO_PRIORITY
      >,
      number
    >;
    total: number;
  };
};

async function fetchConversations({
  searchTerm,
  priority,
  type,
  limit = 15,
  cursor,
}: FetchConversationsParams): Promise<FetchConversationsResponse> {
  const params: any = { limit };

  if (searchTerm) params.search = searchTerm;
  if (priority) params.priority = priority;
  if (type) params.type = type;
  if (cursor) params.cursor = cursor;

  const { data } = await axios.get(`${alyviaCoreServiceApi}/conversations`, {
    params,
  });
  return data;
}

export function useConversations(
  filters: FetchConversationsParams
): UseInfiniteQueryResult<InfiniteData<FetchConversationsResponse[], Error>> {
  return useInfiniteQuery({
    queryKey: ["conversations", filters],
    queryFn: ({ pageParam = null }) =>
      fetchConversations({ ...filters, cursor: pageParam }),
    enabled: !!filters,
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      return lastPage?.meta.hasMore ? lastPage.meta.nextCursor : undefined;
    },
    // getPreviousPageParam: (firstPage) => {
    //   return firstPage?.meta ? firstPage?.data[0]?.id : null;
    // },
    placeholderData: (prevData) => prevData,
    refetchInterval: 10000,
    staleTime: 5000, // Time that data is considered "fresh" before a new fetch
  });
}

async function patchConversation(
  conversationId: string,
  conversationPriority: ConversationPriorityEnum = ConversationPriorityEnum.LOW,
  categorizedAt?: Date
): Promise<{ message: string }> {
  const { data } = await axios.patch(
    `${alyviaCoreServiceApi}/conversations/${conversationId}`,
    {
      priority: conversationPriority,
      ...(categorizedAt && {
        lastCategorizedAt: categorizedAt,
      }),
    }
  );
  return data;
}

export function useConversationPatch() {
  const { setConversationFilter, conversationFilter } = useChatStore();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      conversationPriority = ConversationPriorityEnum.LOW,
      categorizedAt,
    }: {
      conversationId: string;
      conversationPriority?: ConversationPriorityEnum;
      categorizedAt?: Date;
    }) =>
      patchConversation(conversationId, conversationPriority, categorizedAt),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ["messages", data.conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      const newPriority = data.conversationPriority as unknown as ConversationFilterEnum
      newPriority !== conversationFilter && setConversationFilter(newPriority)
    },
  });
}

type data = {
  deviceId: string;
  range: DateRangeEnum;
  startDate?: string; // ISO
  endDate?: string; // ISO
};

export type SummarizeConversationResponse = {
  from: string;
  summary: string;
  to: string;
  totalMessages: number;
};

export function summarizeConversation(
  conversationId: string,
  data: data,
): Promise<SummarizeConversationResponse> {
  return axios
    .post(
      `${alyviaCoreServiceApi}/conversations/${conversationId}/summarize`,
      data,
    )
    .then((response) => response.data);
}
